import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ICallFlowStep,
  IClinicInternalExtension,
  IClinicNumber,
} from '@dataTypes/clinic.type'
import {
  ActionButton,
  Button,
  Checkbox,
  InputField,
  Select,
  AudioPlayer,
} from '@enamelai/design-system'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import { Icon as UploadIcon } from '@enamelai/design-system/icons/Upload'
import {
  getStepPanelHoldMusicPreviewSrc,
  STEP_PANEL_HOLD_MUSIC_OPTIONS,
  STEP_PANEL_TIMEOUT_OPTIONS,
} from '@constants/Settings'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import StepPanelContainer from './StepPanelContainer.component'

const AUDIO_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/*'
const MAX_HOLD_AUDIO_BYTES = 50 * 1024 * 1024
const INVALID_HOLD_AUDIO_MSG = 'Invalid file. Please upload a valid audio file'
const INVALID_HOLD_AUDIO_SIZE_MSG = 'File is too large. Maximum size is 50 MB.'

const AUDIO_EXT_RE = /\.(mp3|wav|m4a|aac|ogg|flac|webm|opus)$/i

const isAudioMimeOrExtension = (file: File): boolean => {
  if (file.type && file.type.startsWith('audio/')) return true
  return AUDIO_EXT_RE.test(file.name)
}

const EXTENSION_NA = 'N/A' as const

const isUnsetOrNA = (v: string | null | undefined): boolean =>
  v == null || String(v).trim() === '' || String(v).trim() === 'N/A'

type LocalHoldPreview = { url: string; file: File }

const phoneKeyEqual = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  if (!b || b === 'N/A' || b === '') return a === null
  return normalizePhoneNumber(a ?? '') === normalizePhoneNumber(b)
}

export type AddToQueuePanelProps = {
  step: Extract<ICallFlowStep, { type: 'add_queue' }>
  branchLabel: string
  clinicNumbers: IClinicNumber[]
  internalExtensions: IClinicInternalExtension[]
  aiEnabled?: boolean
  onChange: (
    patch: Partial<Extract<ICallFlowStep, { type: 'add_queue' }>>,
  ) => void
  onCancel: () => void
  onDone: () => void
  onPendingFile?: (file: File | null) => void
}

const AddToQueuePanel = ({
  step,
  branchLabel,
  clinicNumbers,
  internalExtensions,
  aiEnabled = false,
  onChange,
  onCancel,
  onDone,
  onPendingFile,
}: AddToQueuePanelProps): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [queueValidationMessages, setQueueValidationMessages] = useState<
    Record<string, string>
  >({})
  const holdMusicFileRef = useRef<HTMLInputElement>(null)
  const holdMusicPreviewRef = useRef<LocalHoldPreview | null>(null)
  const [localHoldMusicPreview, setLocalHoldMusicPreview] =
    useState<LocalHoldPreview | null>(null)
  const [holdMusicFileError, setHoldMusicFileError] = useState<string | null>(
    null,
  )
  const timeoutOptions = useMemo(
    () =>
      STEP_PANEL_TIMEOUT_OPTIONS.filter(
        (option) => option.value !== 'ai_receptionist' || aiEnabled,
      ),
    [aiEnabled],
  )

  const clearHoldMusicUrlValidation = useCallback(() => {
    setQueueValidationMessages((prev) => {
      if (!prev.holdMusicCustomAudioUrl) return prev
      const next = { ...prev }
      delete next.holdMusicCustomAudioUrl
      return next
    })
  }, [])

  useEffect(() => {
    if (step.informPosition === undefined) {
      onChangeRef.current({ informPosition: false })
    }
  }, [step.informPosition, step.id])

  const replaceHoldMusicPreview = useCallback(
    (next: LocalHoldPreview | null) => {
      if (holdMusicPreviewRef.current) {
        URL.revokeObjectURL(holdMusicPreviewRef.current.url)
      }
      holdMusicPreviewRef.current = next
      setLocalHoldMusicPreview(next)
    },
    [],
  )

  useEffect(() => {
    if (!step.holdMusicCustomFileName?.trim()) {
      replaceHoldMusicPreview(null)
    }
  }, [step.holdMusicCustomFileName, replaceHoldMusicPreview])

  useEffect(() => {
    return () => {
      if (holdMusicPreviewRef.current) {
        URL.revokeObjectURL(holdMusicPreviewRef.current.url)
        holdMusicPreviewRef.current = null
      }
    }
  }, [])

  const applyCustomHoldMusicFile = useCallback(
    (file: File | undefined) => {
      if (!file) return
      if (!isAudioMimeOrExtension(file)) {
        setHoldMusicFileError(INVALID_HOLD_AUDIO_MSG)
        return
      }
      if (file.size > MAX_HOLD_AUDIO_BYTES) {
        setHoldMusicFileError(INVALID_HOLD_AUDIO_SIZE_MSG)
        return
      }
      setHoldMusicFileError(null)
      clearHoldMusicUrlValidation()
      onPendingFile?.(file)
      replaceHoldMusicPreview({
        url: URL.createObjectURL(file),
        file,
      })
      onChange({
        holdMusicCustomFileName: file.name,
        holdMusicCustomFileSizeBytes: file.size,
      })
    },
    [
      clearHoldMusicUrlValidation,
      onChange,
      onPendingFile,
      replaceHoldMusicPreview,
    ],
  )

  const clearCustomHoldMusicFile = useCallback(() => {
    setHoldMusicFileError(null)
    onPendingFile?.(null)
    replaceHoldMusicPreview(null)
    onChange({
      holdMusicCustomFileName: undefined,
      holdMusicCustomFileSizeBytes: undefined,
      holdMusicCustomAudioUrl: undefined,
    })
    if (holdMusicFileRef.current) {
      holdMusicFileRef.current.value = ''
    }
  }, [onChange, onPendingFile, replaceHoldMusicPreview])

  const openHoldMusicFilePicker = useCallback(() => {
    holdMusicFileRef.current?.click()
  }, [])

  const holdMusicPlaybackSrc = useMemo(() => {
    const id = step.holdMusicId
    if (!id || id === 'none') return ''
    if (id === 'custom') {
      return (
        localHoldMusicPreview?.url ??
        (step.holdMusicCustomAudioUrl?.trim() || '')
      )
    }
    return getStepPanelHoldMusicPreviewSrc(id)?.trim() ?? ''
  }, [
    step.holdMusicId,
    step.holdMusicCustomAudioUrl,
    localHoldMusicPreview?.url,
  ])

  const hasReroutePhone = Boolean(step.rerouteNumber?.trim())
  const bothReroutePhoneAndExtensionUnset =
    isUnsetOrNA(step.rerouteNumber) && isUnsetOrNA(step.rerouteExtension)

  const reroutePhoneOptions = useMemo(() => {
    const fromClinic = [
      { label: 'N/A', value: 'N/A' },
      ...clinicNumbers
        .filter((n) => n.phone_number)
        .map((n) => ({
          label: formatPhoneNumber(n.phone_number),
          value: n.phone_number,
        })),
    ]
    const current = step.rerouteNumber
    if (current && !fromClinic.some((o) => o.value === current)) {
      return [
        {
          label: formatPhoneNumber(current) || current,
          value: current,
        },
        ...fromClinic,
      ]
    }
    return fromClinic
  }, [clinicNumbers, step.rerouteNumber])

  const extensionsForReroutePhone = useMemo(() => {
    const pn = step.rerouteNumber ?? ''
    return internalExtensions
      .filter((e) => e.extension_number && phoneKeyEqual(e.phone_number, pn))
      .sort((a, b) =>
        a.extension_number.localeCompare(b.extension_number, undefined, {
          numeric: true,
        }),
      )
  }, [internalExtensions, step.rerouteNumber])

  const lineExtensionForReroute = useMemo(() => {
    if (!hasReroutePhone || !step.rerouteNumber?.trim()) return undefined
    const line = clinicNumbers.find(
      (n) =>
        n.phone_number && phoneKeyEqual(n.phone_number, step.rerouteNumber!),
    )
    return line?.extension_number?.trim() || undefined
  }, [clinicNumbers, hasReroutePhone, step.rerouteNumber])

  const hasNoRegisterableExtension = useMemo(
    () =>
      hasReroutePhone &&
      extensionsForReroutePhone.length === 0 &&
      !lineExtensionForReroute,
    [hasReroutePhone, extensionsForReroutePhone, lineExtensionForReroute],
  )

  const rerouteExtensionOptions = useMemo(() => {
    if (!hasReroutePhone || !step.rerouteNumber?.trim()) return []
    const fromInternal = extensionsForReroutePhone.map((e) => ({
      label: e.extension_number,
      value: e.extension_number,
    }))
    const selectedLine = clinicNumbers.find((n) =>
      phoneKeyEqual(n.phone_number, step.rerouteNumber!),
    )
    const lineExt = lineExtensionForReroute
    const base = [...fromInternal]
    if (lineExt && !base.some((o) => o.value === lineExt)) {
      base.unshift({
        label: selectedLine?.line_name?.trim()
          ? `${selectedLine.line_name.trim()} — ${lineExt}`
          : lineExt,
        value: lineExt,
      })
    }
    if (base.length === 0) {
      return [{ label: 'N/A', value: EXTENSION_NA }]
    }
    const ext = step.rerouteExtension
    if (ext && ext !== EXTENSION_NA && !base.some((o) => o.value === ext)) {
      return [{ label: ext, value: ext }, ...base]
    }
    return base
  }, [
    hasReroutePhone,
    step.rerouteExtension,
    step.rerouteNumber,
    clinicNumbers,
    extensionsForReroutePhone,
    lineExtensionForReroute,
  ])

  useLayoutEffect(() => {
    if (!hasReroutePhone) return
    if (hasNoRegisterableExtension) {
      if (step.rerouteExtension !== EXTENSION_NA) {
        setQueueValidationMessages({})
        onChangeRef.current({ rerouteExtension: EXTENSION_NA })
      }
    } else if (step.rerouteExtension === EXTENSION_NA) {
      setQueueValidationMessages({})
      onChangeRef.current({ rerouteExtension: undefined })
    }
  }, [
    hasReroutePhone,
    step.rerouteExtension,
    step.rerouteNumber,
    hasNoRegisterableExtension,
  ])

  const rerouteExtOnlyNa =
    hasReroutePhone &&
    rerouteExtensionOptions.length === 1 &&
    rerouteExtensionOptions[0]!.value === EXTENSION_NA

  const rerouteExtDropdownValue = useMemo(() => {
    if (!hasReroutePhone) return null
    if (rerouteExtensionOptions.length === 0) return null
    if (rerouteExtOnlyNa) return EXTENSION_NA
    const v = step.rerouteExtension
    if (!v || v === EXTENSION_NA) return null
    return v
  }, [
    hasReroutePhone,
    rerouteExtOnlyNa,
    step.rerouteExtension,
    rerouteExtensionOptions,
  ])

  const patchReroute = (
    patch: Partial<Extract<ICallFlowStep, { type: 'add_queue' }>>,
  ): void => {
    setQueueValidationMessages({})
    onChange(patch)
  }

  const handleDone = (): void => {
    const errs: Record<string, string> = {}
    if (!step.team?.trim()) {
      errs.team = 'Select a team.'
    }
    if (step.informPosition == null) {
      errs.informPosition =
        'Select whether to inform the caller about their number in queue.'
    }
    if (!step.holdMusicId?.trim()) {
      errs.holdMusicId = 'Select hold music.'
    }
    if (step.onTimeout == null || String(step.onTimeout).trim() === '') {
      errs.onTimeout = 'Select an action when wait time is exceeded.'
    }
    if (
      step.maxWaitSec == null ||
      Number.isNaN(step.maxWaitSec) ||
      step.maxWaitSec <= 0
    ) {
      errs.maxWaitSec = 'Invalid seconds.'
    }
    const hasCustomHoldMusic =
      Boolean(step.holdMusicCustomAudioUrl?.trim()) ||
      Boolean(step.holdMusicCustomFileName?.trim()) ||
      Boolean(localHoldMusicPreview?.file)

    if (step.holdMusicId === 'custom' && !hasCustomHoldMusic) {
      errs.holdMusicCustomAudioUrl =
        'Hold music audio URL is required for custom hold music.'
    }
    if (step.onTimeout === 'reroute' && bothReroutePhoneAndExtensionUnset) {
      errs.rerouteNumber = 'Select a phone number or extension for reroute.'
    }
    if (Object.keys(errs).length > 0) {
      setQueueValidationMessages(errs)
      return
    }
    setQueueValidationMessages({})
    onDone()
  }

  return (
    <StepPanelContainer
      title="Add to queue"
      branchLabel={branchLabel}
      onCancel={onCancel}
      onDone={handleDone}
    >
      <div className="scp-row">
        <div className="scp-panel-field">
          <Select
            label="Team"
            placeholder="Select team"
            options={teams.map((team) => ({
              label: team,
              value: team,
            }))}
            value={step.team || undefined}
            error={!!queueValidationMessages.team}
            helperText={queueValidationMessages.team}
            onChange={(value) => {
              setQueueValidationMessages({})
              const next = String(value || '')
              onChange({ team: next || undefined })
            }}
          />
        </div>
        <div className="scp-panel-field scp-max-wait-time">
          <InputField
            variant="number"
            fullWidth
            label="Max wait time"
            numberMin={1}
            placeholder="300"
            clearable={false}
            value={
              step.maxWaitSec != null && !Number.isNaN(step.maxWaitSec)
                ? String(step.maxWaitSec)
                : ''
            }
            trailingIcon={<span>sec</span>}
            onChange={(e) => {
              setQueueValidationMessages({})
              const raw = e.target.value
              onChange({
                maxWaitSec: raw === '' ? undefined : Math.floor(Number(raw)),
              })
            }}
            error={!!queueValidationMessages.maxWaitSec}
            helperText={queueValidationMessages.maxWaitSec}
          />
        </div>
      </div>
      <div className="scp-panel-field">
        <div className="scp-switch-label">
          <Checkbox
            checked={!!step.informPosition}
            onChange={(event) => {
              setQueueValidationMessages({})
              onChange({ informPosition: event.target.checked })
            }}
            label="Inform caller about their number in queue"
          />
          {queueValidationMessages.informPosition && (
            <span className="hint-text" role="alert">
              {queueValidationMessages.informPosition}
            </span>
          )}
        </div>
      </div>
      <div className="scp-panel-field">
        <Select
          label="Hold music"
          placeholder="Select music"
          options={STEP_PANEL_HOLD_MUSIC_OPTIONS.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          value={step.holdMusicId || undefined}
          error={!!queueValidationMessages.holdMusicId}
          helperText={queueValidationMessages.holdMusicId}
          onChange={(value) => {
            const next = String(value || '') || undefined
            setHoldMusicFileError(null)
            setQueueValidationMessages({})
            if (next !== 'custom') {
              onPendingFile?.(null)
              replaceHoldMusicPreview(null)
              onChange({
                holdMusicId: next,
                holdMusicCustomFileName: undefined,
                holdMusicCustomFileSizeBytes: undefined,
                holdMusicCustomAudioUrl: undefined,
              })
              if (holdMusicFileRef.current) {
                holdMusicFileRef.current.value = ''
              }
            } else {
              onChange({ holdMusicId: next })
            }
          }}
        />
        {step.holdMusicId === 'custom' ? (
          <div className="scp-add-queue-custom-hold">
            <input
              ref={holdMusicFileRef}
              type="file"
              accept={AUDIO_ACCEPT}
              className="scp-step-file-input-hidden"
              aria-hidden
              tabIndex={-1}
              onChange={(e) => {
                applyCustomHoldMusicFile(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            {(holdMusicFileError ||
              queueValidationMessages.holdMusicCustomAudioUrl) && (
              <span className="hint-text" role="alert">
                {holdMusicFileError ||
                  queueValidationMessages.holdMusicCustomAudioUrl}
              </span>
            )}
            <div className="d-flex flex-row-reverse align-items-start gap-2 flex-wrap">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leftIcon={
                  <UploadIcon width={16} height={16} aria-hidden="true" />
                }
                onClick={openHoldMusicFilePicker}
              >
                Upload audio
              </Button>
              {step.holdMusicCustomFileName?.trim() ? (
                <div className="scp-add-queue-hold-file-meta">
                  <span className="scp-add-queue-hold-file-name">
                    {step.holdMusicCustomFileName?.trim().split('/').pop()}
                  </span>
                  <ActionButton
                    size="sm"
                    tone="danger"
                    variant="outline"
                    label="Remove audio"
                    icon={
                      <CloseIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    }
                    onClick={clearCustomHoldMusicFile}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {holdMusicPlaybackSrc ? (
          <div className="scp-add-queue-hold-player scp-play-audio-field">
            <AudioPlayer
              key={holdMusicPlaybackSrc}
              src={holdMusicPlaybackSrc}
            />
          </div>
        ) : null}
      </div>
      <div className="scp-panel-field">
        <Select
          label="If wait time exceeded"
          placeholder="Select action"
          options={timeoutOptions}
          value={step.onTimeout || undefined}
          error={!!queueValidationMessages.onTimeout}
          helperText={queueValidationMessages.onTimeout}
          onChange={(value) => {
            setQueueValidationMessages({})
            const next = String(value || '') || undefined
            if (next !== 'reroute') {
              onChange({
                onTimeout: next,
                rerouteNumber: undefined,
                rerouteExtension: undefined,
              })
              return
            }
            onChange({ onTimeout: next })
          }}
        />
      </div>
      {step.onTimeout === 'reroute' ? (
        <>
          <div className="scp-row">
            <div className="scp-panel-field">
              <Select
                label="Redirected number"
                options={reroutePhoneOptions}
                value={step.rerouteNumber || undefined}
                placeholder={
                  reroutePhoneOptions.length <= 1
                    ? 'No phone lines in phone management'
                    : 'Select phone number'
                }
                error={!!queueValidationMessages.rerouteNumber}
                helperText={queueValidationMessages.rerouteNumber}
                onChange={(value) => {
                  const next = String(value)
                  patchReroute({
                    rerouteNumber: next,
                    rerouteExtension: undefined,
                  })
                }}
              />
            </div>
            <div
              className="scp-panel-field dial-number-extension"
              title={!hasReroutePhone ? 'Select phone number' : undefined}
            >
              <Select
                label="Extension no."
                options={rerouteExtensionOptions}
                value={rerouteExtDropdownValue || undefined}
                disabled={!hasReroutePhone}
                placeholder={
                  !hasReroutePhone
                    ? 'Select phone number first'
                    : rerouteExtOnlyNa
                      ? 'N/A'
                      : 'Select extension'
                }
                onChange={(value) =>
                  patchReroute({
                    rerouteExtension: String(value) || undefined,
                  })
                }
              />
            </div>
          </div>
        </>
      ) : null}
    </StepPanelContainer>
  )
}

export default withTeamCatalog(AddToQueuePanel)
